// frontend/src/pages/ResetPassword.jsx
import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Paper, TextField, Button, Typography, Box, Alert, CircularProgress,
  InputAdornment, IconButton, Link as MuiLink
} from '@mui/material';
import { Visibility, VisibilityOff, Lock as LockIcon } from '@mui/icons-material';
import authAPI from '../api/authAPI';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) { setError('This reset link is missing its token — request a new one.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }

    setLoading(true);
    try {
      await authAPI.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err?.error || 'Failed to reset password — the link may have expired');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', p: 3 }}>
      <Paper elevation={0} sx={{ width: '100%', maxWidth: 440, p: { xs: 3, sm: 5 }, borderRadius: 3, boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
          <Box component="img" src="/images/brand/narevek-crm-logo-mark.png" alt="Narevek CRM" sx={{ width: 180 }} />
        </Box>
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', textAlign: 'center' }}>Reset your password</Typography>
        <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5, mb: 4, textAlign: 'center' }}>
          Choose a new password for your account.
        </Typography>

        {done ? (
          <Alert severity="success">Password reset successfully. Redirecting you to sign in…</Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal" required fullWidth autoFocus
              label="New Password" type={showPwd ? 'text' : 'password'}
              value={password} onChange={(e) => setPassword(e.target.value)} disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" color="action" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPwd(s => !s)} edge="end" size="small">
                      {showPwd ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            <TextField
              margin="normal" required fullWidth
              label="Confirm New Password" type={showPwd ? 'text' : 'password'}
              value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} disabled={loading}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockIcon fontSize="small" color="action" /></InputAdornment>
              }}
            />
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            <Button
              type="submit" fullWidth variant="contained" size="large" disabled={loading}
              sx={{
                mt: 3, mb: 2, py: 1.4, fontWeight: 700, fontSize: 16, textTransform: 'none',
                background: 'linear-gradient(135deg, #1e3a8a 0%, #1d4ed8 100%)',
              }}
            >
              {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Reset Password'}
            </Button>
          </Box>
        )}

        <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
          <MuiLink component={RouterLink} to="/login">Back to Sign In</MuiLink>
        </Typography>
      </Paper>
    </Box>
  );
}
